import { ReactNode, useEffect, useRef, useState } from "react"
import invariant from "tiny-invariant"
import {
  BusinessTypeEnum,
  SubscriptionBillingCycleEnum,
} from "~/__generated__/graphql"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useSubscriptionTiers } from "~/common/useSubscriptionTiers"
import { displayErrors } from "~/common/validations"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import Text, { H1 } from "~/ui/typography"
import { flushEvents, trackEvent } from "~/util/tracking"
import { CREATE_CHECKOUT_SESSION } from "../api"

// Set once we hand off to Stripe. The checkout's cancel_url returns the user to
// this screen, so without a guard they would be bounced straight back into
// Stripe and could never decline. When the flag is already present on mount we
// render the manual picker instead. Scoped per tier so a cancelled checkout for
// one tier doesn't suppress the redirect for another.
export const checkoutStorageKey = (tierKey: string) =>
  `${tierKey}-checkout-initiated`

/**
 * For self-serve sign-ups that arrive with a pre-selected tier (set on the user
 * as `signupTierKey` during onboarding), skip the plan picker and send the user
 * straight to Stripe checkout for that tier's monthly price. Falls back to the
 * provided picker if the plan can't be resolved or the user cancels.
 */
export const AutoCheckoutRedirect = ({
  tierKey,
  fallback,
}: {
  tierKey: string
  fallback: ReactNode
}) => {
  const currentUser = useCurrentUser()
  const businessType = currentUser.agency?.businessType
  const billingCycle =
    currentUser.signupBillingCycle || SubscriptionBillingCycleEnum.Monthly
  const { subscriptionTiers, loading, error } = useSubscriptionTiers(
    businessType || BusinessTypeEnum.Brand
  )
  const [createCheckoutSession] = useSafeMutation(CREATE_CHECKOUT_SESSION)
  const startedRef = useRef(false)
  const [showFallback, setShowFallback] = useState(
    () => sessionStorage.getItem(checkoutStorageKey(tierKey)) === "true"
  )

  useEffect(() => {
    if (startedRef.current || showFallback || loading) return
    if (error || !businessType) {
      setShowFallback(true)
      return
    }

    const tier = subscriptionTiers.find(
      (candidate) =>
        candidate.key === tierKey && candidate.billingCycle === billingCycle
    )
    if (!tier) {
      // Unexpected (the pre-selected plan should exist) — let them choose.
      setShowFallback(true)
      return
    }

    startedRef.current = true
    sessionStorage.setItem(checkoutStorageKey(tierKey), "true")
    ;(async () => {
      const result = await createCheckoutSession({
        variables: { stripePriceId: tier.stripePriceId },
      })

      if (result.errors) {
        console.error("Errors creating checkout session:", result.errors)
        displayErrors(result.errors)
        sessionStorage.removeItem(checkoutStorageKey(tierKey))
        startedRef.current = false
        setShowFallback(true)
        return
      }

      invariant(result.data, "Expected data from createCheckoutSession")

      trackEvent("onboarding_step_completed", {
        step: "choose_plan",
        step_number: 3,
      })
      trackEvent("checkout_started", {
        tier: tier.key,
        billing_cycle: tier.billingCycle,
      })
      await flushEvents()

      window.location.href = result.data.checkoutSessionCreate.sessionUrl
    })()
  }, [
    businessType,
    billingCycle,
    subscriptionTiers,
    loading,
    error,
    showFallback,
    tierKey,
    createCheckoutSession,
  ])

  if (showFallback) return <>{fallback}</>

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <H1 className="mb-2">Taking you to checkout…</H1>
      <Text variant="header3" className="mb-8">
        Hang tight while we redirect you to complete your subscription.
      </Text>
      <LoadingIndicatorCentered />
    </div>
  )
}
